import { last, pull, sortBy } from "lodash";
import { createContext } from "react";

export interface DocumentHeadEntry {
  /**
   * Number of `<Page>`s rendered around the page that owns this entry
   */
  depth: number;
  title: string;
  faviconUrl: string;
}

/**
 * Number of `<Page>`s rendered above the current component
 */
export const PageDepthContext = createContext(0);

const FAVICON_LINK_ATTRIBUTE = "data-page-favicon";

const mountedEntries: DocumentHeadEntry[] = [];

/**
 * Writes the active entry to the document: the innermost page wins, and of
 * pages at the same depth, the last one mounted.
 * With no page mounted, the title stays and the page favicon is removed.
 */
function applyDocumentHead() {
  // sortBy is stable, so equal depths keep their mount order
  const activeEntry = last(sortBy(mountedEntries, (entry) => entry.depth));
  let faviconLink = document.head.querySelector<HTMLLinkElement>(
    `link[${FAVICON_LINK_ATTRIBUTE}]`
  );

  if (!activeEntry) {
    faviconLink?.remove();
    return;
  }

  if (document.title !== activeEntry.title) {
    document.title = activeEntry.title;
  }

  if (!faviconLink) {
    faviconLink = document.createElement("link");
    faviconLink.rel = "icon";
    faviconLink.setAttribute(FAVICON_LINK_ATTRIBUTE, "");
    // after any favicon in index.html, so this one is used
    document.head.appendChild(faviconLink);
  }

  if (faviconLink.getAttribute("href") !== activeEntry.faviconUrl) {
    faviconLink.setAttribute("href", activeEntry.faviconUrl);
  }
}

/**
 * Adds a page's title and favicon to the document head.
 * Returns a function that removes it again (restoring the next active page's values).
 */
export function mountDocumentHeadEntry(entry: DocumentHeadEntry): () => void {
  mountedEntries.push(entry);
  applyDocumentHead();

  return () => {
    pull(mountedEntries, entry);
    applyDocumentHead();
  };
}
